import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ClearTasks } from '@actions/tasks.actions';
import { ClearUploadItems } from '@actions/uploads.actions';

import { AuthorizationService, Logout, UnsetSelection } from '@mediahaven/shared';
import { LoadingBehaviorSubject, ShCacheInterceptor, User } from '@stylehaven/components';
import { ApplicationState } from 'state';

@Injectable({ providedIn: 'root' })
export class BootstrapSandboxService {
    isAuthenticated$: Observable<boolean>;
    isTokenValid$: Observable<boolean>;
    isTokenLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();

    // Injectors
    private readonly cacheInterceptor: ShCacheInterceptor = inject(ShCacheInterceptor);

    constructor(
        private readonly authorizationService: AuthorizationService,
        private readonly store: Store<ApplicationState>
    ) {
        // TODO: move Auth guard to shared, refactor these streams to use shared code instead
        this.isAuthenticated$ = this.buildIsAuthenticatedStream();
        this.isTokenValid$ = this.buildIsTokenValidStream();
    }

    public clearStore(): void {
        this.cacheInterceptor?.clearCache();
        this.store.dispatch(new Logout());
        this.store.dispatch(new ClearUploadItems());
        this.store.dispatch(new ClearTasks());
        this.store.dispatch(new UnsetSelection());
    }

    private buildIsAuthenticatedStream(): Observable<boolean> {
        return this.store.select(state => state.authentication.isAuthenticated);
    }

    private fetchUserIfAuthenticated(isAuthenticated: boolean): Observable<boolean> {
        if (isAuthenticated) {
            return this.fetchUserInfo().pipe(
                map(currentUser => !!currentUser),
                catchError(() => of(false))
            );
        } else {
            return of(false);
        }
    }

    private fetchUserInfo(): Observable<User> {
        return this.authorizationService.getUserInfo();
    }

    private buildIsTokenValidStream(): Observable<boolean> {
        return this.isAuthenticated$.pipe(
            tap(() => this.isTokenLoading$.startLoading()),
            switchMap(this.fetchUserIfAuthenticated.bind(this)),
            tap(() => this.isTokenLoading$.stopLoading())
        );
    }
}
